import React, { useCallback, useEffect, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import ReportV2 from "../components/report/ReportV2";
import { getReportV2, modifyReportByQuery } from "../lib/serverComm";
import ReportConfigSidebarV2 from "../components/report/designer/ReportConfigSidebarV2";
// import { deepMerge } from "../lib/utils";
import QueryBar from "../components/chat/QueryBar";

import { Toast } from "@/components/ui/toast.tsx";
import { useCurrentWorkspace } from "@/store/authContext/authContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { InfoIcon } from "lucide-react";
import EventBus from "@/lib/eventBus";

import {
  initialReportDesignerState,
  reducer as reportDesignerStateReducer,
  type ReportDesignerState,
  type ReportStateReducerActionType,
} from "@/common/reportDesignerStateReducer.ts";

interface ReportDesignerV2Props {
  onReportSave?: (reportId: string) => void;
  disableNavigationOnSave?: boolean;
}

const ReportDesignerV2: React.FC<ReportDesignerV2Props> = ({
  onReportSave,
  disableNavigationOnSave = false,
}) => {
  const { reportId: id } = useParams();
  const workspace = useCurrentWorkspace();
  const initialDataset = workspace.dataset_access.filter(
    (d) =>
      !["recs", "attrition_directional_drivers", "attrition_drivers"].includes(
        d,
      ),
  )[0];

  const reducerWithOverrides = useCallback(
    (state: ReportDesignerState, action: ReportStateReducerActionType) =>
      reportDesignerStateReducer(
        state,
        action,
        workspace.dataset_field_configs,
      ),
    [workspace.dataset_field_configs],
  );
  const [state, dispatch] = useReducer(
    reducerWithOverrides,
    initialReportDesignerState(initialDataset),
  );

  const [isToastOpen, setIsToastOpen] = useState(false);
  const [toastTitle, setToastTitle] = useState("");
  const [toastDescription, setToastDescription] = useState("");

  // Set the report ID when it changes
  useEffect(() => {
    dispatch({ type: "setReportId", payload: id || null });
  }, [id]);

  // Fetch report config when reportId changes
  useEffect(() => {
    if (state.reportId) {
      // Fetch config when reportId changes
      const fetchConfig = async () => {
        try {
          const response = await getReportV2(state.reportId as string);
          EventBus.$emit("report:configFetched", response.config);
          dispatch({ type: "setReportConfig", payload: response.config });
        } catch {
          dispatch({
            type: "setError",
            payload: "There was a problem fetching the report configuration.",
          });
        }
      };
      fetchConfig();
    }
  }, [state.reportId]);

  useEffect(() => {
    setToastTitle(state.toastTitle || "");
    setToastDescription(state.toastDescription || "");
    if (state.toastTitle || state.toastDescription) {
      setIsToastOpen(true);
    }
  }, [state.toastErrorIndex, state.toastTitle, state.toastDescription]);

  const [isQuerying, setIsQuerying] = useState(false);

  // Load initial report config and set correct dataset
  useEffect(() => {
    EventBus.$on("report:resetReport", () => {
      dispatch({ type: "resetState" });
    });
    EventBus.$on("report:saved", (reportId: string) => {
      if (onReportSave) {
        onReportSave(reportId);
      }
    });
    EventBus.$on("report:yAxisLabelClick", (label: string) => {
      dispatch({
        type: "yAxisLabelClick",
        payload: label,
      });
    });
    EventBus.$on("report:xAxisLabelClick", (rawDate: string) => {
      dispatch({
        type: "xAxisLabelClick",
        payload: rawDate,
      });
    });
    return () => {
      EventBus.$off("report:resetReport");
      EventBus.$off("report:saved");
      EventBus.$off("report:yAxisLabelClick");
      EventBus.$off("report:xAxisLabelClick");
    };
  }, [id, dispatch, onReportSave]);

  const handleQuerySubmit = async (query: string) => {
    setIsQuerying(true);

    try {
      const currentConfig = state.reportConfig;
      if (!currentConfig) return;

      const data = await modifyReportByQuery(currentConfig, query, id);
      if (data.reportConfig) {
        dispatch({
          type: "setReportConfig",
          payload: data.reportConfig,
        });
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "An unknown error occurred";
      dispatch({ type: "setError", payload: message });
    } finally {
      setIsQuerying(false);
    }
  };

  // Only render the report once we have initialized the config with the correct dataset
  if (state.isInitializing) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">Loading report configuration...</div>
      </div>
    );
  }

  if (state.isError) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">
          {state.errorMessage || "Failed to load report configuration."}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <Toast
        open={isToastOpen}
        setOpen={setIsToastOpen}
        title={toastTitle}
        description={toastDescription}
      />
      <ScrollArea className="flex-grow overflow-scroll">
        <div className="p-8">
          <div className="relative">
            <ReportV2
              id={id}
              reportConfig={state.reportConfig}
              newFilterField={state.newFilterDefinitionName}
              hasChanges={state.hasChanges}
              reportDesignerDispatch={dispatch}
              onSnapshotViewChange={() => {}}
              disableNavigationOnSave={disableNavigationOnSave}
            />
          </div>
        </div>
        <div className="relative mb-8">
          <QueryBar
            onSubmit={handleQuerySubmit}
            isLoading={isQuerying}
            suggestions={[]}
            id={id}
            placeholder={
              state.reportId
                ? "How would you like to modify this report?"
                : "What sort of report can I help you create?"
            }
          />
        </div>
      </ScrollArea>
      <div className="w-96 bg-white dark:bg-gray-800 flex-shrink-0">
        <ReportConfigSidebarV2
          reportDesignerState={state}
          reportDesignerDispatch={dispatch}
        />
      </div>
      {state.isError && (
        <div className="fixed bottom-4 right-4 bg-muted/50 p-3 rounded-md flex items-center gap-2 text-sm text-muted-foreground">
          <InfoIcon className="h-4 w-4" />
          <div>{state.errorMessage}</div>
        </div>
      )}
    </div>
  );
};

export default ReportDesignerV2;
